import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import {
  completeMissionCardLifecycle,
  transitionMissionCardLifecycle,
  type MissionState,
} from '../lib/missions/lifecycle';
import { appendMissionLifecycleInboxEvent } from '../lib/inbox/events';

type LifecycleTransitionResult = Awaited<ReturnType<typeof transitionMissionCardLifecycle>>;

export function useMissionTransition() {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const queryClient = useQueryClient();

  const invalidateMissionQueries = useCallback((missionId: string) => {
    queryClient.invalidateQueries({ queryKey: ['missions'] });
    queryClient.invalidateQueries({ queryKey: ['mission', missionId] });
  }, [queryClient]);

  const transitionMissionState = useCallback(async (
    missionId: string,
    state: MissionState,
    options?: { reason?: string; snoozedUntil?: number }
  ) => {
    if (!store || !isReady || !wallet) {
      throw new Error('Store not ready or not authenticated');
    }

    const result = await transitionMissionCardLifecycle({
      store,
      userId: wallet.address,
      missionId,
      to: state,
      reason: options?.reason,
      snoozedUntil: options?.snoozedUntil,
    });
    await appendLifecycleInboxProjection(store, wallet.address, missionId, result);
    invalidateMissionQueries(missionId);

    return result;
  }, [store, isReady, wallet, invalidateMissionQueries]);

  const completeMissionState = useCallback(async (missionId: string) => {
    if (!store || !isReady || !wallet) {
      throw new Error('Store not ready or not authenticated');
    }

    const result = await completeMissionCardLifecycle({
      store,
      userId: wallet.address,
      missionId,
    });
    await appendLifecycleInboxProjection(store, wallet.address, missionId, result);
    invalidateMissionQueries(missionId);

    return result;
  }, [store, isReady, wallet, invalidateMissionQueries]);

  return {
    transitionMissionState,
    completeMissionState,
    isReady,
  };
}

export async function appendLifecycleInboxProjection(
  store: Parameters<typeof appendMissionLifecycleInboxEvent>[0]['store'],
  userId: string,
  missionId: string,
  result: LifecycleTransitionResult
): Promise<void> {
  const latestTransition = result.lifecycle.transitions[result.lifecycle.transitions.length - 1];
  if (!latestTransition) return;

  const raw = result.card as unknown as Record<string, unknown>;
  await appendMissionLifecycleInboxEvent({
    store,
    userId,
    missionId,
    from: latestTransition.from,
    to: latestTransition.to,
    title: typeof raw.title === 'string' ? raw.title : undefined,
    createdAt: latestTransition.at,
  }).catch((error) => {
    console.warn('[useMissionTransition] Failed to append lifecycle Inbox event:', error);
  });
}
